function destinationCatalogRequestUrl(catalogUrl, sectionId, page = 1) {
  const url = new URL(catalogUrl, window.location.origin);
  url.searchParams.set('page', String(page));
  url.searchParams.set('sections', sectionId);
  url.searchParams.delete('section_id');
  return url;
}

function delayedDestinationPlanSkeleton(skeleton, enabled) {
  if (!skeleton || !enabled) return () => {};
  const timer = window.setTimeout(() => { skeleton.hidden = false; }, 150);
  return () => {
    window.clearTimeout(timer);
    skeleton.hidden = true;
  };
}

class DestinationPlanTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.tabs = [...this.querySelectorAll('[data-plan-tab]')];
    this.panels = new Map(
      [...this.querySelectorAll('[data-plan-panel]')].map((panel) => [panel.dataset.planPanel, panel]),
    );
    if (!this.tabs.length || !this.panels.size) return;

    this.activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.planTab
      || this.tabs[0].dataset.planTab;
    this.compactMedia = window.matchMedia('(max-width: 1019px)');
    this.handleMediaChange = () => this.render();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.planTab));
    });
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => this.handleKeyboard(event));
    if (this.compactMedia.addEventListener) {
      this.compactMedia.addEventListener('change', this.handleMediaChange);
    } else {
      this.compactMedia.addListener(this.handleMediaChange);
    }

    this.classList.add('is-enhanced');
    this.render();
    this.loadFilteredCatalogs();
  }

  disconnectedCallback() {
    if (!this.compactMedia || !this.handleMediaChange) return;
    if (this.compactMedia.removeEventListener) {
      this.compactMedia.removeEventListener('change', this.handleMediaChange);
    } else {
      this.compactMedia.removeListener(this.handleMediaChange);
    }
  }

  activate(tabName, moveFocus = false) {
    if (!this.panels.has(tabName)) return;
    this.activeTab = tabName;
    this.render();
    if (moveFocus) this.tabs.find((tab) => tab.dataset.planTab === tabName)?.focus();
  }

  render() {
    const compact = this.compactMedia.matches;
    this.dataset.activeTab = this.activeTab;
    this.tabs.forEach((tab) => {
      const selected = tab.dataset.planTab === this.activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    this.panels.forEach((panel, tabName) => {
      panel.hidden = compact && tabName !== this.activeTab;
    });
  }

  handleKeyboard(event) {
    const currentTab = event.target.closest('[data-plan-tab]');
    const currentIndex = this.tabs.indexOf(currentTab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    if (nextIndex === currentIndex && event.key !== 'Home' && event.key !== 'End') return;

    event.preventDefault();
    this.activate(this.tabs[nextIndex].dataset.planTab, true);
  }

  async fetchCatalog(catalogUrl) {
    const url = destinationCatalogRequestUrl(catalogUrl, this.dataset.sectionId);
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Filtered plan catalog returned ${response.status}.`);
    const sections = await response.json();
    const html = sections[this.dataset.sectionId];
    if (typeof html !== 'string') throw new Error('Filtered plan catalog did not return section markup.');
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async loadFilteredCatalogs() {
    if (!this.dataset.sectionId || !this.dataset.fixedCatalogUrl || !this.dataset.unlimitedCatalogUrl) return;

    const fixedResults = this.querySelector('[data-fixed-plan-results]');
    const unlimitedPanel = this.panels.get('day');
    await Promise.all([
      this.loadFixedCatalog(fixedResults),
      this.loadUnlimitedCatalog(unlimitedPanel),
    ]);
  }

  async loadFixedCatalog(target) {
    if (!target || !this.dataset.fixedCatalogUrl) return;
    const emptyState = this.querySelector('[data-fixed-plan-empty]');
    const stopSkeleton = delayedDestinationPlanSkeleton(
      this.querySelector('[data-fixed-plan-skeleton]'),
      target.children.length === 0,
    );
    if (emptyState) emptyState.hidden = true;
    target.setAttribute('aria-busy', 'true');
    try {
      const parsed = await this.fetchCatalog(this.dataset.fixedCatalogUrl);
      const source = parsed.querySelector('[data-fixed-plan-results]');
      if (!source) throw new Error('Filtered fixed plans were missing from section markup.');
      const content = source.cloneNode(true);
      target.replaceChildren(...content.childNodes);
      window.displayCurrency?.renderPrices(target);
    } catch (error) {
      console.error('Fixed plans could not be loaded.', error);
    } finally {
      stopSkeleton();
      if (emptyState) emptyState.hidden = target.children.length > 0;
      target.removeAttribute('aria-busy');
    }
  }

  async loadUnlimitedCatalog(panel) {
    if (!panel || !this.dataset.unlimitedCatalogUrl) return;
    const current = panel.querySelector('[data-unlimited-selector]');
    const emptyState = panel.querySelector('[data-unlimited-plan-empty]');
    const stopSkeleton = delayedDestinationPlanSkeleton(
      panel.querySelector('[data-unlimited-plan-skeleton]'),
      !current,
    );
    if (emptyState) emptyState.hidden = true;
    panel.setAttribute('aria-busy', 'true');
    try {
      const parsed = await this.fetchCatalog(this.dataset.unlimitedCatalogUrl);
      const source = parsed.querySelector('[data-unlimited-selector]');

      if (!source) {
        current?.remove();
        if (emptyState) emptyState.hidden = false;
        return;
      }

      if (current?.syncCatalog) {
        current.syncCatalog(source);
      } else {
        current?.remove();
        emptyState?.remove();
        panel.querySelector('[data-unlimited-plan-anchor]')?.after(source.cloneNode(true));
      }
    } catch (error) {
      console.error('Day plans could not be loaded.', error);
    } finally {
      stopSkeleton();
      const hasSelector = Boolean(panel.querySelector('[data-unlimited-selector]'));
      if (emptyState && !hasSelector) emptyState.hidden = false;
      panel.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('plan-group-tabs')) {
  customElements.define('plan-group-tabs', DestinationPlanTabs);
}

class UnlimitedPlanSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.durationInput = this.querySelector('[data-duration-input]');
    this.durationDecrease = this.querySelector('[data-duration-decrease]');
    this.durationIncrease = this.querySelector('[data-duration-increase]');
    this.planName = this.querySelector('[data-plan-name]');
    this.price = this.querySelector('[data-selected-price]');
    this.discountBadge = this.querySelector('[data-selected-discount]');
    this.discountLabel = this.querySelector('[data-selected-discount-label]');
    this.availability = this.querySelector('[data-availability]');
    this.variantInput = this.querySelector('[data-selected-variant]');
    this.validityDaysProperty = this.querySelector('[data-validity-days-property]');
    this.durationProperty = this.querySelector('[data-duration-property]');
    this.buyButton = this.querySelector('[data-buy-button]');
    this.optionsContainer = this.querySelector('.unlimited-selector__products');
    this.options = [...this.querySelectorAll('[data-unlimited-option]')];
    this.dialog = this.querySelector('[data-calendar-dialog]');
    this.months = this.querySelector('[data-calendar-months]');
    this.monthLabel = this.querySelector('[data-calendar-month-label]');
    this.calendarDuration = this.querySelector('[data-calendar-duration]');
    this.calendarDates = this.querySelector('[data-calendar-dates]');
    this.calendarPrice = this.querySelector('[data-calendar-price]');
    this.today = this.startOfDay(new Date());
    this.applied = { start: this.today, duration: this.validDuration(this.dataset.initialDays) };
    this.applied.end = this.addDays(this.applied.start, this.applied.duration - 1);
    this.visibleMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    this.monthFormatter = this.dateFormatter({ month: 'long', year: 'numeric' });
    this.dateFormatterLong = this.dateFormatter({ month: 'short', day: 'numeric', year: 'numeric' });
    this.dateFormatterShort = this.dateFormatter({ month: 'short', day: 'numeric' });
    this.weekdayFormatter = this.dateFormatter({ weekday: 'narrow' });
    this.totalPages = Math.max(1, Number.parseInt(this.dataset.totalPages, 10) || 1);
    this.currentPage = Math.max(1, Number.parseInt(this.dataset.currentPage, 10) || 1);
    this.catalogLoaded = this.totalPages <= 1;
    this.catalogPromise = null;

    this.bindEvents();
    this.renderApplied();
  }

  startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return this.startOfDay(result);
  }

  toKey(date) {
    return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
  }

  fromKey(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  validDuration(value) {
    const parsed = Number.parseInt(value, 10);
    return Math.max(1, Math.min(90, Number.isFinite(parsed) ? parsed : 1));
  }

  dateFormatter(options) {
    try {
      return new Intl.DateTimeFormat(this.dataset.dateLocale || 'en', options);
    } catch (_) {
      return new Intl.DateTimeFormat('en', options);
    }
  }

  formatDays(days) {
    const template = Number(days) === 1 ? this.dataset.dayTemplate : this.dataset.daysTemplate;
    return template.replace('__days__', days);
  }

  findOption(duration) {
    return this.options.find((option) => Number(option.dataset.days) === duration);
  }

  ensureAllOptions() {
    if (this.catalogLoaded) return Promise.resolve();
    if (this.catalogPromise) return this.catalogPromise;

    const pages = Array.from({ length: this.totalPages }, (_, index) => index + 1)
      .filter((page) => page !== this.currentPage);
    this.catalogPromise = Promise.all(pages.map((page) => this.loadOptionsPage(page)))
      .then((pageOptions) => this.mergeOptions(pageOptions.flat()))
      .catch((error) => console.error('Unlimited plan options could not be loaded.', error))
      .finally(() => {
        this.catalogLoaded = true;
      });
    return this.catalogPromise;
  }

  async loadOptionsPage(page) {
    const url = destinationCatalogRequestUrl(
      this.dataset.catalogUrl || window.location.href,
      this.dataset.sectionId,
      page,
    );
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Section page ${page} returned ${response.status}.`);
    const sections = await response.json();
    const html = sections[this.dataset.sectionId];
    if (typeof html !== 'string') throw new Error(`Section page ${page} did not return markup.`);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return [...parsed.querySelectorAll('[data-unlimited-option]')];
  }

  mergeOptions(options) {
    const knownDurations = new Set(this.options.map((option) => Number(option.dataset.days)));
    options.forEach((option) => {
      const duration = Number(option.dataset.days);
      if (!Number.isInteger(duration) || duration < 1 || duration > 90 || knownDurations.has(duration)) return;
      const clone = option.cloneNode(true);
      this.optionsContainer.append(clone);
      this.options.push(clone);
      knownDurations.add(duration);
    });
  }

  syncCatalog(source) {
    const options = [...source.querySelectorAll('[data-unlimited-option]')]
      .map((option) => option.cloneNode(true));
    this.dataset.catalogUrl = source.dataset.catalogUrl;
    this.dataset.totalPages = source.dataset.totalPages;
    this.dataset.currentPage = source.dataset.currentPage;
    this.totalPages = Math.max(1, Number.parseInt(this.dataset.totalPages, 10) || 1);
    this.currentPage = Math.max(1, Number.parseInt(this.dataset.currentPage, 10) || 1);
    this.catalogLoaded = this.totalPages <= 1;
    this.catalogPromise = null;
    this.optionsContainer.replaceChildren(...options);
    this.options = options;
    this.renderApplied();
  }

  bindEvents() {
    this.durationInput.addEventListener('input', () => {
      if (!this.durationInput.value) return;
      this.setDuration(this.durationInput.value);
    });
    this.durationInput.addEventListener('blur', () => this.setDuration(this.durationInput.value || this.applied.duration));
    this.durationDecrease.addEventListener('click', () => this.adjustDuration(-1));
    this.durationIncrease.addEventListener('click', () => this.adjustDuration(1));
    this.querySelector('[data-calendar-open]').addEventListener('click', (event) => this.openCalendar(event.currentTarget));
    this.querySelector('[data-calendar-close]').addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-calendar-previous]').addEventListener('click', () => this.moveMonth(-1));
    this.querySelector('[data-calendar-next]').addEventListener('click', () => this.moveMonth(1));
    this.querySelector('[data-calendar-apply]').addEventListener('click', () => this.applyCalendar());
    this.months.addEventListener('click', (event) => {
      const button = event.target.closest('[data-calendar-date]');
      if (button && !button.disabled) this.selectCalendarDate(this.fromKey(button.dataset.calendarDate));
    });
    this.months.addEventListener('keydown', (event) => this.handleCalendarKeyboard(event));
    this.dialog.addEventListener('close', () => {
      this.draft = null;
      this.renderApplied();
    });
    this.compactMedia = window.matchMedia('(max-width: 760px)');
    this.compactMedia.addEventListener?.('change', () => {
      if (this.dialog.open) this.renderCalendar();
    });
  }

  setDuration(value) {
    const duration = this.validDuration(value);
    this.applied.duration = duration;
    this.applied.end = this.addDays(this.applied.start, duration - 1);
    this.renderApplied();
  }

  adjustDuration(offset) {
    if (offset < 0) {
      this.durationInput.stepDown();
    } else {
      this.durationInput.stepUp();
    }
    this.setDuration(this.durationInput.value);
    this.durationInput.focus();
  }

  renderOptionPrice(container, option) {
    const price = document.createElement('span');
    price.dataset.priceUsdCents = option.dataset.priceUsdCents;
    price.textContent = option.dataset.priceFormatted || '';
    const comparePrice = Number(option.dataset.comparePriceUsdCents);
    if (comparePrice > Number(option.dataset.priceUsdCents)) {
      const regularLabel = document.createElement('span');
      regularLabel.className = 'visually-hidden';
      regularLabel.textContent = `${this.dataset.regularPriceLabel}: `;
      const regularPrice = document.createElement('s');
      regularPrice.className = 'unlimited-selector__compare-price';
      regularPrice.dataset.priceUsdCents = option.dataset.comparePriceUsdCents;
      regularPrice.textContent = option.dataset.comparePriceFormatted || '';
      const saleLabel = document.createElement('span');
      saleLabel.className = 'visually-hidden';
      saleLabel.textContent = `${this.dataset.salePriceLabel}: `;
      container.replaceChildren(regularLabel, regularPrice, saleLabel, price);
    } else {
      container.replaceChildren(price);
    }
    window.displayCurrency?.renderPrices(container);
  }

  renderDiscountBadge(option) {
    const comparePrice = Number(option?.dataset.comparePriceUsdCents);
    const salePrice = Number(option?.dataset.priceUsdCents);
    const hasDiscount = comparePrice > salePrice;
    this.discountBadge.hidden = !hasDiscount;
    if (!hasDiscount) return;
    const percent = Math.round(((comparePrice - salePrice) / comparePrice) * 100);
    this.discountLabel.textContent = this.dataset.saveTemplate.replace('__percent__', String(percent));
  }

  renderApplied() {
    const { duration } = this.applied;
    const option = this.findOption(duration);
    this.durationInput.value = duration;
    this.durationDecrease.disabled = duration <= Number(this.durationInput.min);
    this.durationIncrease.disabled = duration >= Number(this.durationInput.max);
    this.validityDaysProperty.value = String(duration);
    this.durationProperty.value = this.formatDays(duration);
    this.planName.textContent = this.dataset.calendarPlanTemplate.replace('__duration__', this.formatDays(duration));

    if (!option && !this.catalogLoaded) {
      this.variantInput.value = '';
      this.price.textContent = '';
      this.price.removeAttribute('data-price-usd-cents');
      this.renderDiscountBadge(null);
      this.buyButton.disabled = true;
      this.availability.hidden = false;
      this.availability.textContent = this.dataset.loadingLabel;
      this.ensureAllOptions().then(() => this.renderApplied());
      return;
    }

    if (!option || option.dataset.available !== 'true') {
      this.variantInput.value = '';
      this.price.textContent = '';
      this.price.removeAttribute('data-price-usd-cents');
      this.renderDiscountBadge(null);
      this.buyButton.disabled = true;
      this.availability.hidden = false;
      this.availability.textContent = this.dataset.unavailableLabel;
      return;
    }

    this.variantInput.value = option.dataset.variantId;
    this.price.removeAttribute('data-price-usd-cents');
    this.renderOptionPrice(this.price, option);
    this.renderDiscountBadge(option);
    this.availability.textContent = '';
    this.availability.hidden = true;
    this.buyButton.disabled = false;
  }

  openCalendar(trigger) {
    this.draft = { ...this.applied };
    this.visibleMonth = new Date(this.draft.start.getFullYear(), this.draft.start.getMonth(), 1);
    this.dialog.__returnFocus = trigger;
    this.renderCalendar();
    this.dialog.showModal();
    this.ensureAllOptions().then(() => {
      if (this.dialog.open) this.renderCalendarSummary(this.draft || this.applied);
    });
  }

  moveMonth(offset) {
    const candidate = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + offset, 1);
    const firstCurrentMonth = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    if (candidate >= firstCurrentMonth) this.visibleMonth = candidate;
    this.renderCalendar();
  }

  selectCalendarDate(date) {
    if (date < this.today) return;
    if (!this.draft.start || this.draft.end) {
      this.draft = { start: date, end: null, duration: 1 };
    } else if (date < this.draft.start) {
      this.draft = { start: date, end: null, duration: 1 };
    } else {
      const duration = Math.floor((date - this.draft.start) / 86400000) + 1;
      if (duration > 90) return;
      this.draft.end = date;
      this.draft.duration = duration;
    }
    this.renderCalendar();
  }

  applyCalendar() {
    const duration = this.draft.duration;
    this.applied = {
      start: this.draft.start,
      end: this.draft.end || this.draft.start,
      duration,
    };
    this.dialog.close();
  }

  renderCalendar() {
    const draft = this.draft || this.applied;
    const monthCount = this.compactMedia?.matches ? 1 : 2;
    const monthNames = [];
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < monthCount; index += 1) {
      const month = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + index, 1);
      monthNames.push(this.monthFormatter.format(month));
      fragment.append(this.buildMonth(month, draft));
    }
    this.months.replaceChildren(fragment);
    this.monthLabel.textContent = monthNames.join(' / ');
    this.querySelector('[data-calendar-previous]').disabled = this.visibleMonth.getFullYear() === this.today.getFullYear() && this.visibleMonth.getMonth() === this.today.getMonth();
    this.renderCalendarSummary(draft);
  }

  buildMonth(month, draft) {
    const container = document.createElement('section');
    container.className = 'destination-calendar__month';
    const heading = document.createElement('h3');
    heading.textContent = this.monthFormatter.format(month);
    const weekdays = document.createElement('div');
    weekdays.className = 'destination-calendar__weekdays';
    for (let day = 0; day < 7; day += 1) {
      const item = document.createElement('span');
      item.textContent = this.weekdayFormatter.format(new Date(2024, 0, 7 + day));
      item.setAttribute('aria-hidden', 'true');
      weekdays.append(item);
    }
    const grid = document.createElement('div');
    grid.className = 'destination-calendar__days';
    const leading = month.getDay();
    for (let empty = 0; empty < leading; empty += 1) grid.append(document.createElement('span'));
    const finalDate = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    for (let day = 1; day <= finalDate; day += 1) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const button = document.createElement('button');
      const key = this.toKey(date);
      const rangeStart = draft.start;
      const rangeEnd = draft.end || draft.start;
      const maximumEnd = this.addDays(draft.start, 89);
      const beforeStart = date < this.today;
      const exceedsRange = Boolean(draft.start && !draft.end && date > maximumEnd);
      button.type = 'button';
      button.textContent = day;
      button.dataset.calendarDate = key;
      button.setAttribute('aria-label', this.dateFormatterLong.format(date));
      button.disabled = beforeStart || exceedsRange;
      if (key === this.toKey(rangeStart) || key === this.toKey(rangeEnd)) {
        button.classList.add('is-range-end');
        button.setAttribute('aria-selected', 'true');
      }
      if (date > rangeStart && date < rangeEnd) button.classList.add('is-in-range');
      if (key === this.toKey(this.today)) button.classList.add('is-today');
      grid.append(button);
    }
    container.append(heading, weekdays, grid);
    return container;
  }

  renderCalendarSummary(draft) {
    const end = draft.end || draft.start;
    const option = this.findOption(draft.duration);
    this.calendarDuration.textContent = this.dataset.calendarPlanTemplate.replace('__duration__', this.formatDays(draft.duration));
    this.calendarDates.textContent = `${this.dateFormatterShort.format(draft.start)} – ${this.dateFormatterShort.format(end)}`;
    if (!option && !this.catalogLoaded) {
      this.calendarPrice.removeAttribute('data-price-usd-cents');
      this.calendarPrice.textContent = this.dataset.loadingLabel;
      this.ensureAllOptions().then(() => {
        if (this.dialog.open) this.renderCalendarSummary(this.draft || this.applied);
      });
      return;
    }
    if (option && option.dataset.available === 'true') {
      this.calendarPrice.removeAttribute('data-price-usd-cents');
      this.renderOptionPrice(this.calendarPrice, option);
    } else {
      this.calendarPrice.removeAttribute('data-price-usd-cents');
      this.calendarPrice.textContent = '';
    }
  }

  handleCalendarKeyboard(event) {
    const active = event.target.closest('[data-calendar-date]');
    if (!active) return;
    const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (!(event.key in offsets)) return;
    event.preventDefault();
    const next = this.addDays(this.fromKey(active.dataset.calendarDate), offsets[event.key]);
    if (next < this.today) return;
    if (this.draft?.start && !this.draft.end && next > this.addDays(this.draft.start, 89)) return;
    const currentLastMonth = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + (this.compactMedia?.matches ? 0 : 1) + 1, 0);
    if (next > currentLastMonth || next < this.visibleMonth) {
      this.visibleMonth = new Date(next.getFullYear(), next.getMonth(), 1);
      this.renderCalendar();
    }
    this.months.querySelector(`[data-calendar-date="${this.toKey(next)}"]`)?.focus();
  }
}

if (!customElements.get('unlimited-plan-selector')) {
  customElements.define('unlimited-plan-selector', UnlimitedPlanSelector);
}
